import type { ReactElement } from 'react';
import { useState, useEffect, useCallback } from 'react';
import * as React from 'react';
import { Modal, Button, useNotify } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { disableMfa } from '@hotjar/webapp/domains/mfa/api';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useMfaModals } from '@hotjar/webapp/features/Mfa/useMfaModals';

interface MfaDisableModalProps {
  userId: number;
  onDisabled: () => void;
}

export const MfaDisableModal = ({ userId, onDisabled }: MfaDisableModalProps): ReactElement => {
  const { t } = useTranslationWithPrefix('mfa:disable-modal');
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useNotify();
  const { loadedModal } = useTrack();
  const { disableMfa: isOpen, setDisableMfa } = useMfaModals();
  const onClose = useCallback(() => setDisableMfa(false), [setDisableMfa]);

  useEffect(() => {
    if (isOpen) {
      loadedModal('Disable MFA', undefined, {});
    }
  }, [isOpen, loadedModal]);

  const handleDisableMfa = () => {
    setIsLoading(true);

    disableMfa({ userId })
      .then(() => {
        onDisabled();
        onClose();
        showToast({ text: t('success-message'), kind: 'success' });
      })
      .catch(() => showToast({ text: t('error-message'), kind: 'danger' }))
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal contentLabel={t('content-label')} isOpen={isOpen} onClose={onClose}>
      <Modal.Header title={t('title')} />
      <Modal.Body>
        <p>{t('description')}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button kind="danger" isLoading={isLoading} onClick={handleDisableMfa} type="submit">
          {t('action')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
